import DSBox from '../../design-system/DSBox';
import useKlydoPlayer from './hooks/useKlydoPlayer';
import KlydoAnimation from './KlydoAnimation';
import Pendulum from './Pendulum';
import ClockView from './ClockView';
import { Klydo } from '../../api/klydo';
import { KlydoPreview as KlydoPreviewType } from '../../api/public';
import {
  CLOCK_DIAMETER,
  KLYDO_HEIGHT_WITHOUT_FRAME,
  KLYDO_WIDTH_WITHOUT_FRAME,
} from '../../consts/clockDimensions';
import { ReactNode } from 'react';
import ArtistInfo from '../ArtistInfo/ArtistInfo';
import KlydoInfo from '../ArtistInfo/KlydoInfo';
import { CLOCK_FRAME_BACKGROUND } from '../../consts/colors';
import { resolveLogoDiameterPx } from './resolveLogoDiameterPx';
import { PendulumLogoType } from '../../consts/pendulumLogo';

export type KlydoPlayerView = 'clock' | 'artist' | 'info';

type KlydoPlayerProps = {
  klydo: Partial<Klydo> | KlydoPreviewType;
  dialsHidden?: boolean;
  backgroundHidden?: boolean;
  minutesValue?: number;
  hoursValue?: number;
  isPaused?: boolean;
  scale?: number;
  pendulumLogo?: string;
  pendulumLogoSize?: number;
  // See Pendulum.tsx: once true, logoUrl already arrives pre-sized and no
  // client-side scaling should be applied to it.
  isApprovedCollection?: boolean;
  // See resolveLogoDiameterPx: 'full' fills the whole ball regardless of
  // pendulumLogoSize, matching klydo-api's cover-crop bake for that type.
  pendulumLogoType?: PendulumLogoType;
  pendulumLabel?: string;
  muted?: boolean;
  playSoundOnce?: boolean;
  replaySoundTrigger?: number;
  activeView?: KlydoPlayerView;
  children?: ReactNode;
};

const KlydoPlayer = ({
  klydo,
  isPaused,
  dialsHidden,
  backgroundHidden,
  scale = 1,
  pendulumLogo,
  pendulumLogoSize,
  isApprovedCollection,
  pendulumLogoType,
  pendulumLabel,
  muted,
  playSoundOnce,
  replaySoundTrigger,
  activeView = 'clock',
  children,
  hoursValue,
  minutesValue,
}: KlydoPlayerProps) => {
  const {
    ref,
    backgroundColor,
    loopUrl,
    photoUrl,
    previewFrameSeconds,
    handsColor,
    pendulumColor,
    pendulumRodColor,
    hourHandRef,
    minuteHandRef,
    hourHandShadowRef,
    minuteHandShadowRef,
    dialsColor,
  } = useKlydoPlayer({ klydo, hoursValue, minutesValue });

  const width = `${KLYDO_WIDTH_WITHOUT_FRAME * scale}px`;
  const height = `${KLYDO_HEIGHT_WITHOUT_FRAME * scale}px`;
  const clockDiameter = `${CLOCK_DIAMETER * scale}px`;

  return (
    <DSBox
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <DSBox
        sx={{
          width,
          height,
          aspectRatio: '18 / 30',
          margin: '0 auto',
          borderRadius: '34.827px',
          position: 'relative',
          border: backgroundHidden ? 'none' : '2px solid #2D2D2D',
          padding: '40.971px var(--6, 48px)',
          backgroundColor: backgroundHidden
            ? 'transparent'
            : CLOCK_FRAME_BACKGROUND,
        }}
      >
        {children}
        <DSBox ref={ref}>
          <DSBox
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              display: 'flex',
              width: '100%',
              height: '50%',
              marginTop: '50%',
            }}
          >
            <DSBox
              sx={{
                backgroundColor,
                borderRadius: '50%',
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                width: clockDiameter,
                height: clockDiameter,
                position: 'absolute',
                padding: 0,
              }}
            >
              {activeView === 'artist' && (
                <ArtistInfo
                  klydoPreview={klydo as KlydoPreviewType}
                  scale={scale}
                />
              )}
              {activeView === 'info' && (
                <KlydoInfo
                  klydoPreview={klydo as KlydoPreviewType}
                  scale={scale}
                />
              )}
              {activeView === 'clock' && (
                <>
                  <KlydoAnimation
                    {...(loopUrl
                      ? {
                          loopUrl,
                          isPaused,
                          previewFrameSeconds,
                          muted,
                          playSoundOnce,
                          replaySoundTrigger,
                        }
                      : { photoUrl })}
                  />
                  <ClockView
                    hourHandRef={hourHandRef}
                    hourHandShadowRef={hourHandShadowRef}
                    minuteHandRef={minuteHandRef}
                    minuteHandShadowRef={minuteHandShadowRef}
                    handsColor={handsColor}
                    dialsHidden={dialsHidden}
                    dialsColor={dialsColor}
                    scale={scale}
                  />
                </>
              )}
            </DSBox>
            <Pendulum
              backgroundColor={backgroundColor}
              pendulumColor={pendulumColor}
              rodColor={pendulumRodColor}
              isStatic={isPaused}
              scale={scale}
              pendulumLogo={pendulumLogo}
              pendulumLogoType={pendulumLogoType}
              logoDiameterPx={resolveLogoDiameterPx({
                isApprovedCollection,
                pendulumLogoSize,
                pendulumLogoType,
                scale,
              })}
              pendulumLabel={pendulumLabel}
            />
          </DSBox>
        </DSBox>
      </DSBox>
    </DSBox>
  );
};

export default KlydoPlayer;
